import * as React from 'react';
import { forwardRef, useEffect, useState } from 'react';

import { useLocation, useNavigate } from 'react-router-dom';

import Card from '@mui/material/Card';

import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';

import BasicLayout from 'pages/authentication/components/BasicLayout';
import { useIntl } from 'react-intl';
import * as Yup from 'yup';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup/dist/yup';
import { useSignInMutation } from 'services/authApi';
import { fErrors } from 'utils/formatError';
import { useNotification } from 'hook/useNotification';
import { setCredentials } from 'slice/authSlice';
import { isAuthorized, ROLE_OPERATION_READ } from 'utils/userUtils';
import { getBgImageURL, getImageURL, getUrlParameter } from 'utils/urlUtils';
import app from 'config/app.json';
import { IconButton, InputAdornment, useMediaQuery, useTheme } from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import useConf from 'hook/useConf';
import { useGetConfigurationQuery } from 'services/applicationApi';
import ArcadePortal from 'components/Logo/ArcadePortal';
import { useDispatch } from 'react-redux';
import PersonIcon from '@mui/icons-material/Person';
import KeyIcon from '@mui/icons-material/Key';
import InputWithStartAdornment from 'components/inputs/InputWithStartAdornment';
import { ThemeProvider } from '@mui/material/styles';
import { useThemeBuilder } from 'hook/useThemeBuilder';
import { useMaterialUIController } from 'context';
import * as PropTypes from 'prop-types';

const PasswordWithStartAdornment = forwardRef((props, ref) => {
  const { formatMessage: msg } = useIntl();

  const [passwordVisible, setPasswordVisible] = useState(false);

  const onPasswordVisibleClick = () => {
    setPasswordVisible(!passwordVisible);
  };

  const onPasswordMouseDown = (event) => {
    event.preventDefault();
  };

  return (
    <InputWithStartAdornment
      ref={ref}
      type={passwordVisible ? 'text' : 'password'}
      fullWidth
      autoComplete="current-password"
      adornmentIcon={<KeyIcon fontSize="medium" />}
      label={props.label}
      error={!!props.errors.password}
      helperText={props.errors?.password?.message}
      endAdornment={
        <InputAdornment position="end">
          <IconButton
            aria-label={msg({ id: 'common.toggle.password' })}
            onClick={onPasswordVisibleClick}
            onMouseDown={onPasswordMouseDown}
            edge="end"
          >
            {passwordVisible ? <VisibilityOff /> : <Visibility />}
          </IconButton>
        </InputAdornment>
      }
      {...props}
    />
  );
});

PasswordWithStartAdornment.propTypes = {
  passwordVisible: PropTypes.bool,
  label: PropTypes.string,
  errors: PropTypes.any,
  register: PropTypes.any
};

function AuthenticationCard(props) {
  const { formatMessage: msg } = useIntl();
  const { addNotification } = useNotification();

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const logoCode = `logo.dark`;
  const { getValue: conf, refetch: refetchConf } = useConf(useGetConfigurationQuery);
  const customLogo = conf(`ui.image.${logoCode}`);
  const currentAppCode =
    getUrlParameter(window.location, app.url.paramApp) ?? app.application.defaultCode;
  const [logoSource, setLogoSource] = useState(
    customLogo ? getImageURL(logoCode, currentAppCode) : null
  );

  useEffect(
    () => setLogoSource(customLogo ? getImageURL(logoCode, currentAppCode) : null),
    [currentAppCode, customLogo, logoCode]
  );

  // Keep the whole pre-login location, not just its pathname: navigate() consumes its
  // pathname + search + hash, so the external-identifier deep link (externalId lives in the
  // query string of this HashRouter portal) survives the login round-trip instead of being
  // truncated to the path, which would leave the viewer stuck loading.
  const from = location.state?.from ?? '/';

  const validationSchema = Yup.object().shape({
    login: Yup.string().required(msg({ id: 'signin.login.required' })),
    password: Yup.string().required(msg({ id: 'signin.password.required' }))
  });

  const {
    handleSubmit,
    formState: { errors },
    register
  } = useForm({
    resolver: yupResolver(validationSchema)
  });

  const [signIn, { isLoading }] = useSignInMutation();

  const onSubmit = (credentials) => {
    signIn(credentials)
      .unwrap()
      .then((payload) => {
        const applicationRole = `APPLICATION_${currentAppCode}`;

        if (!isAuthorized(payload, applicationRole, ROLE_OPERATION_READ)) {
          addNotification({
            type: 'warning',
            title: msg({ id: 'notification.warning.title' }),
            message: msg({ id: 'security.insufficientPrivileges' })
          });
          return;
        }

        dispatch(setCredentials(payload));

        // Forces the configuration reloading
        refetchConf();

        // Send them back to the page they tried to visit when they were
        // redirected to the login page. Use { replace: true } so we don't create
        // another entry in the history stack for the login page.  This means that
        // when they get to the protected page and click the back button, they
        // won't end up back on the login page, which is also really nice for the
        // user experience.
        navigate(from, { replace: true });
      })
      .catch((errors) => {
        const errorMessage =
          errors?.status === 401
            ? msg({ id: 'security.invalidCredentials' })
            : fErrors(msg, errors);
        addNotification({
          type: 'error',
          title: msg({ id: 'notification.error.title' }),
          message: errorMessage
        });
      });
  };

  return (
    <Card
      variant="outlined"
      sx={{
        height: { lg: '100%' },
        m: 2,
        opacity: 0.95
      }}
    >
      <MDBox
        bgColor="none"
        borderRadius="lg"
        coloredShadow="none"
        mx={2}
        mt={0}
        p={2}
        mb={1}
        textAlign="center"
      >
        <MDTypography variant="h4" color="white" sx={{ fontWeight: 'medium', mt: 1 }}>
          {logoSource ? (
            <MDBox
              component="img"
              src={logoSource}
              alt="Logo"
              sx={{
                maxWidth: '186px',
                maxHeight: '80px'
              }}
              onError={({ currentTarget }) => {
                currentTarget.onerror = null;
                setLogoSource(null);
              }}
            />
          ) : (
            <ArcadePortal
              primaryColor={props.theme.palette.text.main}
              dark={props.theme.palette.mode === 'dark'}
              width="200"
            />
          )}
        </MDTypography>
        {/* <Grid container spacing={3} justifyContent="center" sx={{ mt: 1, mb: 2 }}> */}
        {/*  <Grid item xs={2}> */}
        {/*    <MDTypography component={MuiLink} href="#" variant="body1" color="white"> */}
        {/*      <FacebookIcon color="inherit" /> */}
        {/*    </MDTypography> */}
        {/*  </Grid> */}
        {/*  <Grid item xs={2}> */}
        {/*    <MDTypography component={MuiLink} href="#" variant="body1" color="white"> */}
        {/*      <GoogleIcon color="inherit" /> */}
        {/*    </MDTypography> */}
        {/*  </Grid> */}
        {/* </Grid> */}
      </MDBox>
      <MDBox pt={4} pb={3} px={3}>
        <MDBox
          component="form"
          role="form"
          noValidate
          onSubmit={handleSubmit(onSubmit)}
          sx={{
            '& input:-internal-autofill-selected, & input:-webkit-autofill, & input:-webkit-autofill:focus':
              {
                transition: 'background-color 600000s 0s, color 600000s 0s'
              }
          }}
        >
          <MDBox mb={2}>
            <InputWithStartAdornment
              autoFocus
              fullWidth
              autoComplete="current-login"
              adornmentIcon={<PersonIcon fontSize="medium" />}
              label={msg({ id: 'signin.login' })}
              error={!!errors.login}
              helperText={errors?.login?.message}
              {...register('login')}
            />
          </MDBox>
          <MDBox mb={2}>
            <PasswordWithStartAdornment
              label={msg({ id: 'signin.password' })}
              errors={errors}
              {...register('password')}
            />
          </MDBox>
          <MDBox mt={4} mb={1}>
            <MDButton
              variant="gradient"
              color="primary"
              fullWidth
              type="submit"
              loading={isLoading}
            >
              {msg({ id: 'action.signin' })}
            </MDButton>
          </MDBox>
          {/* <MDBox mt={3} mb={1} textAlign="center"> */}
          {/*   <MDTypography variant="button" color="text"> */}
          {/*     Don&apos;t have an account?{' '} */}
          {/*     <MDTypography */}
          {/*       component={Link} */}
          {/*       to="/authentication/sign-up" */}
          {/*       variant="button" */}
          {/*       color="info" */}
          {/*       fontWeight="medium" */}
          {/*       textGradient */}
          {/*     > */}
          {/*       Sign up */}
          {/*     </MDTypography> */}
          {/*   </MDTypography> */}
          {/* </MDBox> */}
        </MDBox>
      </MDBox>
    </Card>
  );
}

AuthenticationCard.propTypes = {
  theme: PropTypes.any
};

function Basic() {
  const { formatMessage: msg } = useIntl();
  const theme = useTheme();
  const lgWidthDown = useMediaQuery(theme.breakpoints.down('lg'));

  const { controller } = useMaterialUIController();
  const { primaryColor, secondaryColor } = controller;
  const { darkTheme } = useThemeBuilder(primaryColor, secondaryColor);

  return (
    <ThemeProvider theme={darkTheme}>
      <BasicLayout
        imageURL={getBgImageURL('login.background')}
        title={msg({ id: 'signin.title' })}
        justifyContent={lgWidthDown ? 'center' : 'flex-end'}
        height={{ lg: '60%' }}
      >
        <AuthenticationCard theme={darkTheme} />
      </BasicLayout>
    </ThemeProvider>
  );
}

export default Basic;
